import storage from '@pray/shared/utils/storage';

/**
 * What is playing, written down for the application that answers the next document.
 *
 * pray.com is served by two applications at once while the Next.js rebuild takes it over
 * one URL prefix at a time, and moving between them is a full page load. This player keeps
 * its state in memory, so a reader who taps the logo mid-episode arrives on the other side
 * with silence and no bar. This record is how the bar comes back there: the Next.js Discover
 * reads it once on mount and restores the piece, paused, at the remembered second.
 *
 * **It is a contract with `www.pray.com`, not this app's own state.** The reader is
 * `lib/now-playing.ts` in that repo, and it refuses anything it does not recognise rather
 * than coercing it: a different `v`, a missing field, a non-http audio URL, a write older
 * than 24 hours, a position within 5 seconds of a known end. A field added or renamed here is
 * a field that side has to read too. Change both, or change neither.
 *
 * The key is stored through `storage`, so it takes the same environment prefix every other
 * shared key takes (bare on production and qa-2, `${ENV}/` elsewhere), which is the rule the
 * reader applies to its own side.
 */
export const NOW_PLAYING_KEY = 'nowPlaying';

/** The reader's freshness window, stated here so the two sides can be compared by eye. */
export const NOW_PLAYING_TTL_MS = 24 * 60 * 60 * 1000;

const positiveOrNull = (value) => (typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null);

/**
 * The record for a piece of content and the element playing it, or null when there is
 * nothing worth writing: no content, no id, or an audio URL the reader would refuse.
 *
 * `position` overrides the element's own time, for the callers that know better: a seek
 * writes where it landed, and a page being left writes the time the element still reports.
 */
export function buildNowPlaying({ content, audio, position, previousContentId, nextContentId, now = Date.now() }) {
  if (!content?.id || typeof content.audio_url !== 'string') return null;
  if (!/^https?:\/\//.test(content.audio_url)) return null;

  const explicit = typeof position === 'number' && Number.isFinite(position) && position >= 0;
  const at = explicit ? position : positiveOrNull(audio?.currentTime) ?? 0;

  return {
    v: 1,
    id: content.id,
    title: content.title || '',
    series: content.content_series_title ?? null,
    artwork: content.image_url ?? null,
    audioUrl: content.audio_url,
    duration: positiveOrNull(audio?.duration) ?? positiveOrNull(content.duration),
    position: at,
    updatedAt: now,
    source: 'gatsby',
    nextId: nextContentId ?? content.next_content_id ?? null,
    prevId: previousContentId ?? null,
  };
}

/**
 * The same place means the same position, to the millisecond: an element nobody played reports
 * exactly the time it was left at. Anything wider freezes the time during playback, because the
 * record is also written as it plays, a fraction of a second further on each time.
 */
const SAME_PLACE_SECONDS = 0.001;

/**
 * Writes the record. Returns true when storage holds the right record, written now or
 * deliberately left alone, and false when there was nothing to write or storage refused it.
 */
export function rememberNowPlaying(args) {
  const record = buildNowPlaying(args);
  if (!record) return false;
  /*
   * The time says when the reader last moved, not when a page was last left (APP-205). Every
   * `pagehide` writes this record, played or not, and a fresh time on an unmoved position made
   * a stale place look newest to anything that merges by time, www.pray.com's progress merge
   * among them. So a write with nothing playing and nothing moved changes nothing: what storage
   * holds stands, even when another tab or www.pray.com wrote it since. A play at the saved
   * second is the reader moving again, so it takes a new time, and so is a seek while paused:
   * the newest thing the reader did wins, which is the rule this record has always kept across
   * tabs. Only a write that carries no action of the reader's is held back, and only while
   * storage holds a record: with none, and no write of this document's to take a time from, the
   * record is written with a new time, because restoring the bar matters more than its age.
   */
  // A position handed in is an act (a play, a seek, a tick). A paused element with none is a
  // pause or a page being left: a pause after playback has moved past the last tick, so it
  // still stamps a new time below, and only an unmoved one is held back.
  const idle = args.audio?.paused !== false && typeof args.position !== 'number';
  const stored = readRecord();
  // Moved means moved in this document, and a document that has written nothing has not moved.
  if (idle && stored && (!lastWritten || samePlace(lastWritten, record))) return true;
  if (idle && samePlace(lastWritten, record)) record.updatedAt = lastWritten.updatedAt;
  const saved = storage.upsertToStorage(NOW_PLAYING_KEY, JSON.stringify(record)) === true;
  if (saved) lastWritten = { id: record.id, position: record.position, updatedAt: record.updatedAt };
  return saved;
}

/*
 * This document's own last write, for when storage no longer holds one: refused, cleared, or
 * unreadable. Without it an unmoved position would be stamped as new the moment the record went.
 */
let lastWritten = null;

function samePlace(it, record) {
  return it?.id === record.id && Math.abs(it.position - record.position) < SAME_PLACE_SECONDS;
}

function readRecord() {
  try {
    const record = JSON.parse(storage.getFromStorage(NOW_PLAYING_KEY) || 'null');
    return record && typeof record.updatedAt === 'number' && typeof record.position === 'number' ? record : null;
  } catch (err) {
    // Unreadable or absent: nothing to keep, so the new time is the only one.
    return null;
  }
}

/** Removes it. Closing the player, or hearing a piece out, is a decision the next document should not undo. */
export function forgetNowPlaying() {
  lastWritten = null;
  try {
    storage.removeFromStorage(NOW_PLAYING_KEY);
  } catch (err) {
    // Safari in private mode throws on the accessor. A record we cannot remove is one the
    // reader will judge on its timestamp, which is the fallback the window exists for.
  }
}

export default {
  build: buildNowPlaying,
  remember: rememberNowPlaying,
  forget: forgetNowPlaying,
};
